import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus, Copy, Check, RefreshCw, Ticket, Clock, UserCheck, Trash2,
  CheckCircle, Loader2, Building2, Mail, Send, AlertCircle, Pencil, X, Save, User
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { invokeFunction } from "../../lib/invokeFunction";

function generateCode() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "OBS-";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

function EditInviteForm({ invite, onSave, onCancel }) {
  const [expertName, setExpertName] = useState(invite.invited_name || "");
  const [email, setEmail] = useState(invite.invited_email || "");
  const [company, setCompany] = useState(invite.company_name || "");
  const [tier, setTier] = useState(invite.expert_tier || "invited");
  const [expiresAt, setExpiresAt] = useState(
    invite.expires_at ? new Date(invite.expires_at).toISOString().split("T")[0] : ""
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(invite.id, {
      invited_name: expertName.trim() || "",
      invited_email: email.trim() || null,
      company_name: company.trim() || "",
      expert_tier: tier,
      expires_at: expiresAt ? new Date(expiresAt + "T23:59:59").toISOString() : invite.expires_at,
    });
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-4 pt-4 border-t border-neutral-800"
    >
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">
            Имя спикера
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
            <input
              type="text"
              value={expertName}
              onChange={(e) => setExpertName(e.target.value)}
              placeholder="Имя и фамилия"
              className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">
            Email эксперта
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="expert@company.com"
              className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">
            Компания
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Название компании"
              className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-lg font-extralight text-neutral-400 mb-2">
            Уровень эксперта
          </label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
          >
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>
      <div className="mb-4">
        <label className="block text-lg font-extralight text-neutral-400 mb-2">
          Действует до
        </label>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          className="w-full sm:w-64 px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
        />
      </div>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
        >
          <X className="w-4 h-4" />
          Отмена
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white text-lg font-extralight transition-colors"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </motion.div>
  );
}

export default function InviteCodesTab({ userId }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [newCodeCompany, setNewCodeCompany] = useState("");
  const [newCodeEmail, setNewCodeEmail] = useState("");
  const [newCodeName, setNewCodeName] = useState("");
  const [newCodeTier, setNewCodeTier] = useState("premium");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("invite_codes")
      .select("*")
      .order("created_at", { ascending: false });
    setCodes(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const sendInviteEmail = async (inviteCodeId, email, code, expiresAt, companyName, expertTier, expertName) => {
    try {
      const { data, error } = await invokeFunction("send-invite-email", {
        body: {
          inviteCodeId,
          email,
          code,
          expiresAt,
          companyName,
          expertTier,
          expertName,
        },
      });

      if (error) {
        console.error("Edge function error:", error);
        return { success: false, message: error.message };
      }

      return data || { success: true };
    } catch (err) {
      console.error("Failed to send invite email:", err);
      return { success: false, message: err.message };
    }
  };

  const handleCreate = async () => {
    if (!userId) return;
    setCreating(true);
    setEmailStatus(null);
    const code = generateCode();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 90);

    const insertData = {
      code,
      created_by: userId,
      company_name: newCodeCompany,
      expert_tier: newCodeTier,
      expires_at: expiresAt.toISOString(),
      invited_email: newCodeEmail.trim() || null,
      invited_name: newCodeName.trim() || "",
    };

    const { data: inserted, error } = await supabase
      .from("invite_codes")
      .insert(insertData)
      .select()
      .single();

    if (!error && inserted) {
      if (newCodeEmail.trim()) {
        const emailResult = await sendInviteEmail(
          inserted.id,
          newCodeEmail.trim(),
          code,
          expiresAt.toISOString(),
          newCodeCompany,
          newCodeTier,
          newCodeName.trim()
        );

        if (emailResult.success) {
          setEmailStatus({ type: "success", message: "Приглашение отправлено на " + newCodeEmail.trim() });
        } else {
          setEmailStatus({
            type: "info",
            message: "Код создан. Письмо будет отправлено когда будет настроен сервис рассылки (Resend API Key)."
          });
        }
      }

      setNewCodeCompany("");
      setNewCodeEmail("");
      setNewCodeName("");
      setShowCreateForm(false);
      await fetchCodes();
    }
    setCreating(false);
  };

  const handleSaveEdit = async (id, updates) => {
    const { error } = await supabase
      .from("invite_codes")
      .update(updates)
      .eq("id", id);

    if (!error) {
      setEditingId(null);
      await fetchCodes();
    }
  };

  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (id) => {
    await supabase.from("invite_codes").delete().eq("id", id);
    await fetchCodes();
  };

  const activeCount = codes.filter(c => !c.is_used && new Date(c.expires_at) > new Date()).length;
  const usedCount = codes.filter(c => c.is_used).length;

  return (
    <motion.div
      id="admin_invite-codes-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium text-white">Инвайт-коды</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCodes}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-lg font-extralight transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Обновить
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-extralight transition-colors"
          >
            <Plus className="w-4 h-4" />
            Создать код
          </button>
        </div>
      </div>

      {emailStatus && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-md flex items-start gap-3 ${
            emailStatus.type === "success"
              ? "bg-green-500/10 border border-green-500/20"
              : "bg-primary-500/10 border border-primary-500/20"
          }`}
        >
          {emailStatus.type === "success" ? (
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className={`text-lg font-extralight ${
              emailStatus.type === "success" ? "text-green-400" : "text-primary-400"
            }`}>
              {emailStatus.message}
            </p>
          </div>
          <button
            onClick={() => setEmailStatus(null)}
            className="text-neutral-500 hover:text-white transition-colors"
          >
            &times;
          </button>
        </motion.div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-5 h-5 text-primary-400" />
            <span className="text-lg font-extralight text-neutral-400">Всего кодов</span>
          </div>
          <p className="text-2xl font-medium text-white">{codes.length}</p>
        </div>
        <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-accent-400" />
            <span className="text-lg font-extralight text-neutral-400">Активных</span>
          </div>
          <p className="text-2xl font-medium text-white">{activeCount}</p>
        </div>
        <div className="p-4 rounded-md bg-neutral-900/50 border border-neutral-800">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-5 h-5 text-green-400" />
            <span className="text-lg font-extralight text-neutral-400">Использовано</span>
          </div>
          <p className="text-2xl font-medium text-white">{usedCount}</p>
        </div>
      </div>

      {showCreateForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="p-6 rounded-md bg-neutral-900/50 border border-primary-600/30"
        >
          <h3 className="text-xl font-medium text-white mb-4">Новый инвайт-код</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-lg font-extralight text-neutral-400 mb-2">
                Имя спикера (необязательно)
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                <input
                  type="text"
                  value={newCodeName}
                  onChange={(e) => setNewCodeName(e.target.value)}
                  placeholder="Имя и фамилия"
                  className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
                />
              </div>
              <p className="text-lg font-extralight text-neutral-600 mt-1">
                Используется в приветствии инвайт-письма
              </p>
            </div>
            <div>
              <label className="block text-lg font-extralight text-neutral-400 mb-2">
                Email эксперта (необязательно)
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                <input
                  type="email"
                  value={newCodeEmail}
                  onChange={(e) => setNewCodeEmail(e.target.value)}
                  placeholder="expert@company.com"
                  className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
                />
              </div>
              <p className="text-lg font-extralight text-neutral-600 mt-1">
                Если указан, приглашение отправится автоматически
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-lg font-extralight text-neutral-400 mb-2">
                Компания (необязательно)
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                <input
                  type="text"
                  value={newCodeCompany}
                  onChange={(e) => setNewCodeCompany(e.target.value)}
                  placeholder="Для какой компании"
                  className="w-full pl-10 pr-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-lg font-extralight text-neutral-400 mb-2">
                Уровень эксперта
              </label>
              <select
                value={newCodeTier}
                onChange={(e) => setNewCodeTier(e.target.value)}
                className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight focus:outline-none focus:border-primary-500"
              >
                <option value="premium">Premium</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEmailStatus(null);
              }}
              className="px-4 py-2 rounded-md text-neutral-400 hover:text-white text-lg font-extralight transition-colors"
            >
              Отмена
            </button>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-2 px-6 py-2 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white text-lg font-extralight transition-colors"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : newCodeEmail.trim() ? (
                <Send className="w-4 h-4" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {creating
                ? "Создание..."
                : newCodeEmail.trim()
                ? "Создать и отправить"
                : "Создать"}
            </button>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-neutral-500 animate-spin mx-auto mb-4" />
          <p className="text-lg font-extralight text-neutral-400">Загрузка кодов...</p>
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-12 p-6 rounded-md bg-neutral-900/50 border border-neutral-800">
          <Ticket className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-lg font-extralight text-neutral-400">
            Пока нет инвайт-кодов. Создайте первый.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map((invite) => {
            const expired = new Date(invite.expires_at) < new Date();
            const status = invite.is_used ? "used" : expired ? "expired" : "active";
            const isEditing = editingId === invite.id;

            return (
              <div
                key={invite.id}
                className={`p-4 rounded-md border ${
                  status === "active"
                    ? "bg-neutral-900/50 border-neutral-800"
                    : status === "used"
                    ? "bg-accent-950/20 border-accent-800/30"
                    : "bg-neutral-950/50 border-neutral-800/30 opacity-60"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                    status === "used"
                      ? "bg-green-500/10"
                      : status === "active"
                      ? "bg-primary-500/10"
                      : "bg-neutral-800"
                  }`}>
                    {status === "used" ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <Ticket className={`w-5 h-5 ${status === "active" ? "text-primary-400" : "text-neutral-600"}`} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <code className="text-lg font-medium text-white tracking-wider">
                        {invite.code}
                      </code>
                      <button
                        onClick={() => handleCopy(invite.code, invite.id)}
                        className="flex items-center gap-1 text-neutral-600 hover:text-primary-400 text-sm"
                      >
                        <Copy className="w-4 h-4" />
                        {copiedId === invite.id ? "Скопировано!" : "Скопировать"}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-sm text-neutral-400">
                        {invite.invited_name || "Не указано"}
                      </p>
                      <p className="text-sm text-neutral-600">
                        {invite.invited_email || "Не указано"}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        status === "active" ? "text-primary-400" : 
                        status === "expired" ? "text-accent-400" : "text-neutral-600"
                      }`}>
                        {status === "active" ? "Активен" : 
                         status === "expired" ? "Истек" : "Использован"}
                      </span>
                      {status === "active" && (
                        <span className="text-xs text-neutral-600">
                          {new Date(invite.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-600">
                        {invite.company_name || "Не указано"}
                      </span>
                      <span className={`text-xs font-medium ${
                        invite.expert_tier === "premium" ? "text-accent-400" : "text-neutral-600"
                      }`}>
                        {invite.expert_tier === "premium" ? "Premium" : "Приглашенный"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <button
                        onClick={() => handleSaveEdit(invite.id, {
                          invited_name: invite.invited_name,
                          invited_email: invite.invited_email,
                          company_name: invite.company_name,
                          expert_tier: invite.expert_tier,
                          expires_at: invite.expires_at,
                        })}
                        className="flex items-center gap-1 text-primary-400 hover:text-primary-300 text-sm"
                      >
                        <Check className="w-4 h-4" />
                        Сохранить
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditingId(invite.id)}
                        className="flex items-center gap-1 text-neutral-600 hover:text-primary-400 text-sm"
                      >
                        <Pencil className="w-4 h-4" />
                        Редактировать
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(invite.id)}
                      className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}